import { useState, useEffect } from "react";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from "../components/ui/card";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../components/ui/select";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "../components/ui/tabs";
import { Badge } from "../components/ui/badge";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "../components/ui/dialog";
import { Checkbox } from "../components/ui/checkbox";
import { 
  Calculator, FileText, Download, Printer, Loader2, CheckCircle, 
  AlertCircle, Users, JapaneseYen, Calendar, Clock, Building2,
  ChevronRight, Eye, RefreshCw
} from "lucide-react";
import axios from "axios";
import { API } from "../App";
import { toast } from "sonner";

const SalaryPage = () => {
  const [loading, setLoading] = useState(true);
  const [calculating, setCalculating] = useState(false);
  const [confirming, setConfirming] = useState(false);
  const [activeTab, setActiveTab] = useState("calculation");
  
  // 年月選択
  const currentDate = new Date();
  const [selectedYear, setSelectedYear] = useState(currentDate.getFullYear());
  const [selectedMonth, setSelectedMonth] = useState(currentDate.getMonth() + 1);
  
  // 給与計算データ
  const [summary, setSummary] = useState(null);
  const [calculations, setCalculations] = useState([]);
  const [selectedEmployees, setSelectedEmployees] = useState([]);
  
  // 給与明細ダイアログ
  const [showPayslipDialog, setShowPayslipDialog] = useState(false);
  const [selectedPayslip, setSelectedPayslip] = useState(null);
  const [payslipFormat, setPayslipFormat] = useState("rakuda"); // rakuda or standard

  useEffect(() => {
    fetchSummary();
  }, [selectedYear, selectedMonth]);

  const fetchSummary = async () => {
    setLoading(true);
    try {
      const res = await axios.get(`${API}/salary/summary?year=${selectedYear}&month=${selectedMonth}`);
      setSummary(res.data);
      setCalculations(res.data.calculations || []);
    } catch (error) {
      console.error("Error fetching summary:", error);
    } finally {
      setLoading(false);
    }
  };

  const handleCalculate = async () => {
    setCalculating(true);
    try {
      const res = await axios.post(`${API}/salary/calculate`, {
        year: selectedYear,
        month: selectedMonth,
        employee_ids: selectedEmployees.length > 0 ? selectedEmployees : []
      });
      toast.success(`${res.data.calculated_count}名の給与を計算しました`);
      fetchSummary();
      setSelectedEmployees([]);
    } catch (error) {
      toast.error("給与計算に失敗しました");
    } finally {
      setCalculating(false);
    }
  };

  const handleConfirm = async () => {
    if (!confirm("給与計算を確定しますか？確定後は修正できません。")) return;
    setConfirming(true);
    try {
      const res = await axios.post(`${API}/salary/confirm`, {
        year: selectedYear,
        month: selectedMonth,
        employee_ids: selectedEmployees.length > 0 ? selectedEmployees : []
      });
      toast.success(`${res.data.confirmed_count}名の給与を確定しました`);
      fetchSummary();
      setSelectedEmployees([]);
    } catch (error) {
      toast.error("確定に失敗しました");
    } finally {
      setConfirming(false);
    }
  };

  const openPayslip = async (employeeId) => {
    try {
      const res = await axios.get(`${API}/salary/payslip/${employeeId}?year=${selectedYear}&month=${selectedMonth}`);
      setSelectedPayslip(res.data);
      setShowPayslipDialog(true);
    } catch (error) {
      toast.error("給与明細の取得に失敗しました");
    }
  };

  const printPayslip = () => {
    const printWindow = window.open('', '_blank');
    const content = payslipFormat === "rakuda" 
      ? generateRakudaPayslip(selectedPayslip)
      : generateStandardPayslip(selectedPayslip);
    printWindow.document.write(content);
    printWindow.document.close();
    printWindow.print();
  };

  const generateRakudaPayslip = (data) => {
    const { calculation, company, employee } = data;
    return `
      <!DOCTYPE html>
      <html>
      <head>
        <meta charset="utf-8">
        <title>給与明細書</title>
        <style>
          @page { size: A4; margin: 10mm; }
          body { font-family: 'MS Gothic', 'Hiragino Kaku Gothic Pro', sans-serif; font-size: 10px; margin: 0; padding: 10px; }
          .header { text-align: center; margin-bottom: 10px; border-bottom: 2px solid #000; padding-bottom: 5px; }
          .header h1 { font-size: 18px; margin: 0; }
          .info-row { display: flex; justify-content: space-between; margin-bottom: 10px; }
          .table { width: 100%; border-collapse: collapse; margin-bottom: 10px; }
          .table th, .table td { border: 1px solid #000; padding: 4px; text-align: right; }
          .table th { background: #f0f0f0; text-align: center; }
          .table td.label { text-align: left; background: #fafafa; }
          .section-title { background: #333; color: #fff; padding: 4px; font-weight: bold; text-align: center; }
          .total-row { background: #fffacd; font-weight: bold; }
          .net-salary { font-size: 14px; background: #e0ffe0; }
          .amount { font-family: monospace; }
        </style>
      </head>
      <body>
        <div class="header">
          <h1>給 与 明 細 書</h1>
          <p>${calculation.year}年${calculation.month}月分</p>
        </div>
        
        <div class="info-row">
          <div>
            <strong>${company?.name || ''}</strong><br>
            ${company?.address || ''}
          </div>
          <div style="text-align: right;">
            <strong>${employee?.name || calculation.employee_name}</strong> 様<br>
            社員番号: ${employee?.employee_code || calculation.employee_code || '-'}
          </div>
        </div>
        
        <table class="table">
          <tr><td colspan="6" class="section-title">勤 怠</td></tr>
          <tr>
            <th>出勤日数</th><td class="amount">${calculation.working_days}日</td>
            <th>労働時間</th><td class="amount">${calculation.working_hours}時間</td>
            <th>残業時間</th><td class="amount">${calculation.overtime_hours}時間</td>
          </tr>
          <tr>
            <th>深夜時間</th><td class="amount">${calculation.late_night_hours}時間</td>
            <th>休日時間</th><td class="amount">${calculation.holiday_hours}時間</td>
            <th>欠勤日数</th><td class="amount">${calculation.absence_days}日</td>
          </tr>
        </table>
        
        <table class="table">
          <tr>
            <td colspan="3" class="section-title">支 給</td>
            <td colspan="3" class="section-title">控 除</td>
          </tr>
          <tr>
            <td class="label">基本給</td><td class="amount" colspan="2">¥${calculation.base_salary.toLocaleString()}</td>
            <td class="label">健康保険</td><td class="amount" colspan="2">¥${calculation.health_insurance.toLocaleString()}</td>
          </tr>
          <tr>
            <td class="label">時間外手当</td><td class="amount" colspan="2">¥${calculation.overtime_pay.toLocaleString()}</td>
            <td class="label">介護保険</td><td class="amount" colspan="2">¥${calculation.nursing_insurance.toLocaleString()}</td>
          </tr>
          <tr>
            <td class="label">深夜手当</td><td class="amount" colspan="2">¥${calculation.late_night_pay.toLocaleString()}</td>
            <td class="label">厚生年金</td><td class="amount" colspan="2">¥${calculation.pension.toLocaleString()}</td>
          </tr>
          <tr>
            <td class="label">休日手当</td><td class="amount" colspan="2">¥${calculation.holiday_pay.toLocaleString()}</td>
            <td class="label">雇用保険</td><td class="amount" colspan="2">¥${calculation.employment_insurance.toLocaleString()}</td>
          </tr>
          <tr>
            <td class="label">通勤手当</td><td class="amount" colspan="2">¥${calculation.commute_allowance.toLocaleString()}</td>
            <td class="label">所得税</td><td class="amount" colspan="2">¥${calculation.income_tax.toLocaleString()}</td>
          </tr>
          <tr>
            <td class="label">その他手当</td><td class="amount" colspan="2">¥${calculation.other_allowances.toLocaleString()}</td>
            <td class="label">住民税</td><td class="amount" colspan="2">¥${calculation.resident_tax.toLocaleString()}</td>
          </tr>
          <tr>
            <td class="label"></td><td class="amount" colspan="2"></td>
            <td class="label">その他控除</td><td class="amount" colspan="2">¥${calculation.other_deductions.toLocaleString()}</td>
          </tr>
          <tr class="total-row">
            <td class="label">支給合計</td><td class="amount" colspan="2">¥${calculation.gross_salary.toLocaleString()}</td>
            <td class="label">控除合計</td><td class="amount" colspan="2">¥${calculation.total_deductions.toLocaleString()}</td>
          </tr>
        </table>
        
        <table class="table">
          <tr class="net-salary">
            <td class="label" style="width: 30%;">差引支給額</td>
            <td class="amount" style="font-size: 16px;">¥${calculation.net_salary.toLocaleString()}</td>
          </tr>
        </table>
        
        <p style="text-align: center; margin-top: 20px; font-size: 9px; color: #666;">
          この明細書は給料らくだPRO形式で出力されています
        </p>
      </body>
      </html>
    `;
  };

  const generateStandardPayslip = (data) => {
    const { calculation, company, employee } = data;
    return `
      <!DOCTYPE html>
      <html>
      <head>
        <meta charset="utf-8">
        <title>給与明細書</title>
        <style>
          @page { size: A4; margin: 15mm; }
          body { font-family: 'Hiragino Kaku Gothic Pro', 'Yu Gothic', sans-serif; font-size: 11px; margin: 0; padding: 20px; }
          .header { text-align: center; margin-bottom: 20px; }
          .header h1 { font-size: 20px; margin: 0 0 5px 0; letter-spacing: 0.5em; }
          .period { font-size: 14px; color: #333; }
          .company-info { margin-bottom: 15px; }
          .employee-info { background: #f5f5f5; padding: 10px; margin-bottom: 15px; border-radius: 4px; }
          .section { margin-bottom: 15px; }
          .section-header { background: #2563eb; color: white; padding: 8px 12px; font-weight: bold; border-radius: 4px 4px 0 0; }
          .section-content { border: 1px solid #ddd; border-top: none; padding: 10px; border-radius: 0 0 4px 4px; }
          .row { display: flex; justify-content: space-between; padding: 5px 0; border-bottom: 1px dotted #ddd; }
          .row:last-child { border-bottom: none; }
          .row .label { color: #666; }
          .row .value { font-weight: bold; font-family: monospace; }
          .total-section { background: #fef3c7; border: 2px solid #f59e0b; padding: 15px; border-radius: 4px; text-align: center; }
          .total-section .label { font-size: 14px; }
          .total-section .value { font-size: 24px; font-weight: bold; color: #d97706; }
          .grid { display: grid; grid-template-columns: 1fr 1fr; gap: 15px; }
        </style>
      </head>
      <body>
        <div class="header">
          <h1>給与明細書</h1>
          <p class="period">${calculation.year}年${calculation.month}月分（${calculation.period_start} 〜 ${calculation.period_end}）</p>
        </div>
        
        <div class="company-info">
          <strong>${company?.name || '会社名'}</strong>
        </div>
        
        <div class="employee-info">
          <div style="display: flex; justify-content: space-between;">
            <div><strong>氏名:</strong> ${employee?.name || calculation.employee_name} 様</div>
            <div><strong>社員番号:</strong> ${employee?.employee_code || calculation.employee_code || '-'}</div>
          </div>
        </div>
        
        <div class="section">
          <div class="section-header">勤怠情報</div>
          <div class="section-content">
            <div class="row"><span class="label">出勤日数</span><span class="value">${calculation.working_days} 日</span></div>
            <div class="row"><span class="label">労働時間</span><span class="value">${calculation.working_hours} 時間</span></div>
            <div class="row"><span class="label">残業時間</span><span class="value">${calculation.overtime_hours} 時間</span></div>
            <div class="row"><span class="label">深夜時間</span><span class="value">${calculation.late_night_hours} 時間</span></div>
          </div>
        </div>
        
        <div class="grid">
          <div class="section">
            <div class="section-header">支給</div>
            <div class="section-content">
              <div class="row"><span class="label">基本給</span><span class="value">¥${calculation.base_salary.toLocaleString()}</span></div>
              <div class="row"><span class="label">時間外手当</span><span class="value">¥${calculation.overtime_pay.toLocaleString()}</span></div>
              <div class="row"><span class="label">深夜手当</span><span class="value">¥${calculation.late_night_pay.toLocaleString()}</span></div>
              <div class="row"><span class="label">休日手当</span><span class="value">¥${calculation.holiday_pay.toLocaleString()}</span></div>
              <div class="row"><span class="label">通勤手当</span><span class="value">¥${calculation.commute_allowance.toLocaleString()}</span></div>
              <div class="row"><span class="label">その他手当</span><span class="value">¥${calculation.other_allowances.toLocaleString()}</span></div>
              <div class="row" style="background: #e0f2fe; margin: 5px -10px -10px; padding: 10px; border-radius: 0 0 4px 4px;">
                <span class="label"><strong>支給合計</strong></span>
                <span class="value" style="font-size: 14px;">¥${calculation.gross_salary.toLocaleString()}</span>
              </div>
            </div>
          </div>
          
          <div class="section">
            <div class="section-header" style="background: #dc2626;">控除</div>
            <div class="section-content">
              <div class="row"><span class="label">健康保険</span><span class="value">¥${calculation.health_insurance.toLocaleString()}</span></div>
              <div class="row"><span class="label">介護保険</span><span class="value">¥${calculation.nursing_insurance.toLocaleString()}</span></div>
              <div class="row"><span class="label">厚生年金</span><span class="value">¥${calculation.pension.toLocaleString()}</span></div>
              <div class="row"><span class="label">雇用保険</span><span class="value">¥${calculation.employment_insurance.toLocaleString()}</span></div>
              <div class="row"><span class="label">所得税</span><span class="value">¥${calculation.income_tax.toLocaleString()}</span></div>
              <div class="row"><span class="label">住民税</span><span class="value">¥${calculation.resident_tax.toLocaleString()}</span></div>
              <div class="row" style="background: #fee2e2; margin: 5px -10px -10px; padding: 10px; border-radius: 0 0 4px 4px;">
                <span class="label"><strong>控除合計</strong></span>
                <span class="value" style="font-size: 14px;">¥${calculation.total_deductions.toLocaleString()}</span>
              </div>
            </div>
          </div>
        </div>
        
        <div class="total-section">
          <div class="label">差引支給額</div>
          <div class="value">¥${calculation.net_salary.toLocaleString()}</div>
        </div>
      </body>
      </html>
    `;
  };

  const toggleEmployeeSelection = (employeeId) => {
    setSelectedEmployees(prev => 
      prev.includes(employeeId) 
        ? prev.filter(id => id !== employeeId)
        : [...prev, employeeId]
    );
  };

  const selectAllEmployees = () => {
    if (selectedEmployees.length === calculations.length) {
      setSelectedEmployees([]);
    } else {
      setSelectedEmployees(calculations.map(c => c.employee_id));
    }
  };

  const formatCurrency = (amount) => {
    return `¥${(amount || 0).toLocaleString()}`;
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-[400px]">
        <Loader2 className="w-8 h-8 animate-spin text-emerald-500" />
      </div>
    );
  }

  return (
    <div className="p-4 lg:p-6 space-y-6">
      {/* ヘッダー */}
      <div className="flex flex-col lg:flex-row lg:items-center justify-between gap-4">
        <div>
          <h1 className="text-2xl font-bold text-white flex items-center gap-2">
            <Calculator className="w-7 h-7 text-emerald-500" />
            給与計算
          </h1>
          <p className="text-slate-400 text-sm mt-1">月次給与計算・給与明細書の作成</p>
        </div>
        
        <div className="flex items-center gap-3">
          <Select value={selectedYear.toString()} onValueChange={(v) => setSelectedYear(parseInt(v))}>
            <SelectTrigger className="w-28 bg-slate-800 border-slate-700">
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              {[...Array(5)].map((_, i) => {
                const year = currentDate.getFullYear() - 2 + i;
                return <SelectItem key={year} value={year.toString()}>{year}年</SelectItem>;
              })}
            </SelectContent>
          </Select>
          <Select value={selectedMonth.toString()} onValueChange={(v) => setSelectedMonth(parseInt(v))}>
            <SelectTrigger className="w-24 bg-slate-800 border-slate-700">
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              {[...Array(12)].map((_, i) => (
                <SelectItem key={i + 1} value={(i + 1).toString()}>{i + 1}月</SelectItem>
              ))}
            </SelectContent>
          </Select>
          <Button onClick={fetchSummary} variant="outline" size="icon" className="border-slate-600">
            <RefreshCw className="w-4 h-4" />
          </Button>
        </div>
      </div>

      {/* サマリーカード */}
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
        <Card className="bg-slate-800 border-slate-700">
          <CardContent className="p-4">
            <div className="flex items-center gap-3">
              <div className="p-2 bg-blue-500/20 rounded-lg">
                <Users className="w-5 h-5 text-blue-400" />
              </div>
              <div>
                <p className="text-slate-400 text-sm">対象者数</p>
                <p className="text-xl font-bold text-white">{summary?.total_employees || 0}名</p>
              </div>
            </div>
          </CardContent>
        </Card>
        <Card className="bg-slate-800 border-slate-700">
          <CardContent className="p-4">
            <div className="flex items-center gap-3">
              <div className="p-2 bg-emerald-500/20 rounded-lg">
                <JapaneseYen className="w-5 h-5 text-emerald-400" />
              </div>
              <div>
                <p className="text-slate-400 text-sm">総支給額</p>
                <p className="text-xl font-bold text-white">{formatCurrency(summary?.total_gross_salary)}</p>
              </div>
            </div>
          </CardContent>
        </Card>
        <Card className="bg-slate-800 border-slate-700">
          <CardContent className="p-4">
            <div className="flex items-center gap-3">
              <div className="p-2 bg-red-500/20 rounded-lg">
                <JapaneseYen className="w-5 h-5 text-red-400" />
              </div>
              <div>
                <p className="text-slate-400 text-sm">控除合計</p>
                <p className="text-xl font-bold text-white">{formatCurrency(summary?.total_deductions)}</p>
              </div>
            </div>
          </CardContent>
        </Card>
        <Card className="bg-slate-800 border-slate-700">
          <CardContent className="p-4">
            <div className="flex items-center gap-3">
              <div className="p-2 bg-amber-500/20 rounded-lg">
                <JapaneseYen className="w-5 h-5 text-amber-400" />
              </div>
              <div>
                <p className="text-slate-400 text-sm">差引支給額</p>
                <p className="text-xl font-bold text-white">{formatCurrency(summary?.total_net_salary)}</p>
              </div>
            </div>
          </CardContent>
        </Card>
      </div>

      {/* アクションボタン */}
      <div className="flex flex-wrap gap-3">
        <Button 
          onClick={handleCalculate}
          disabled={calculating}
          className="bg-emerald-600 hover:bg-emerald-700"
        >
          {calculating ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : <Calculator className="w-4 h-4 mr-2" />}
          {selectedEmployees.length > 0 ? `選択者を計算 (${selectedEmployees.length}名)` : "全員を計算"}
        </Button>
        <Button 
          onClick={handleConfirm}
          disabled={confirming || calculations.filter(c => c.status === "draft").length === 0}
          variant="outline"
          className="border-amber-500 text-amber-400 hover:bg-amber-500/10"
        >
          {confirming ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : <CheckCircle className="w-4 h-4 mr-2" />}
          給与確定
        </Button>
      </div>

      {/* 計算結果テーブル */}
      <Card className="bg-slate-800 border-slate-700">
        <CardHeader>
          <div className="flex items-center justify-between">
            <div>
              <CardTitle className="text-white text-lg">給与計算結果</CardTitle>
              <CardDescription>{selectedYear}年{selectedMonth}月分</CardDescription>
            </div>
            {calculations.length > 0 && (
              <Button variant="ghost" size="sm" onClick={selectAllEmployees}>
                {selectedEmployees.length === calculations.length ? "選択解除" : "全選択"}
              </Button>
            )}
          </div>
        </CardHeader>
        <CardContent>
          {calculations.length === 0 ? (
            <div className="text-center py-12 text-slate-400">
              <Calculator className="w-12 h-12 mx-auto mb-3 opacity-50" />
              <p>給与計算データがありません</p>
              <p className="text-sm mt-1">「全員を計算」ボタンをクリックして計算を開始してください</p>
            </div>
          ) : (
            <div className="overflow-x-auto">
              <table className="w-full text-sm">
                <thead>
                  <tr className="border-b border-slate-700">
                    <th className="text-left p-3 text-slate-400 font-medium">
                      <Checkbox 
                        checked={selectedEmployees.length === calculations.length}
                        onCheckedChange={selectAllEmployees}
                      />
                    </th>
                    <th className="text-left p-3 text-slate-400 font-medium">従業員</th>
                    <th className="text-right p-3 text-slate-400 font-medium">出勤</th>
                    <th className="text-right p-3 text-slate-400 font-medium">残業</th>
                    <th className="text-right p-3 text-slate-400 font-medium">支給額</th>
                    <th className="text-right p-3 text-slate-400 font-medium">控除額</th>
                    <th className="text-right p-3 text-slate-400 font-medium">差引支給額</th>
                    <th className="text-center p-3 text-slate-400 font-medium">状態</th>
                    <th className="text-center p-3 text-slate-400 font-medium">操作</th>
                  </tr>
                </thead>
                <tbody>
                  {calculations.map(calc => (
                    <tr key={calc.id} className="border-b border-slate-700/50 hover:bg-slate-700/30">
                      <td className="p-3">
                        <Checkbox 
                          checked={selectedEmployees.includes(calc.employee_id)}
                          onCheckedChange={() => toggleEmployeeSelection(calc.employee_id)}
                        />
                      </td>
                      <td className="p-3">
                        <div className="text-white font-medium">{calc.employee_name}</div>
                        <div className="text-slate-400 text-xs">{calc.employee_code}</div>
                      </td>
                      <td className="p-3 text-right text-white">{calc.working_days}日</td>
                      <td className="p-3 text-right text-amber-400">{calc.overtime_hours}h</td>
                      <td className="p-3 text-right text-emerald-400 font-medium">{formatCurrency(calc.gross_salary)}</td>
                      <td className="p-3 text-right text-red-400">{formatCurrency(calc.total_deductions)}</td>
                      <td className="p-3 text-right text-white font-bold">{formatCurrency(calc.net_salary)}</td>
                      <td className="p-3 text-center">
                        {calc.status === "confirmed" ? (
                          <Badge className="bg-emerald-600">確定</Badge>
                        ) : (
                          <Badge variant="outline" className="border-amber-500 text-amber-400">未確定</Badge>
                        )}
                      </td>
                      <td className="p-3 text-center">
                        <Button 
                          size="sm" 
                          variant="ghost"
                          onClick={() => openPayslip(calc.employee_id)}
                        >
                          <Eye className="w-4 h-4" />
                        </Button>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </CardContent>
      </Card>

      {/* 給与明細ダイアログ */}
      <Dialog open={showPayslipDialog} onOpenChange={setShowPayslipDialog}>
        <DialogContent className="bg-slate-800 border-slate-700 text-white max-w-4xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="flex items-center justify-between">
              <span>給与明細書</span>
              <div className="flex gap-2">
                <Select value={payslipFormat} onValueChange={setPayslipFormat}>
                  <SelectTrigger className="w-40 bg-slate-700 border-slate-600">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="rakuda">給料らくだ形式</SelectItem>
                    <SelectItem value="standard">標準A4形式</SelectItem>
                  </SelectContent>
                </Select>
              </div>
            </DialogTitle>
          </DialogHeader>
          
          {selectedPayslip && (
            <div className="space-y-4">
              <div className="bg-slate-900 p-4 rounded-lg">
                <div className="flex justify-between items-start mb-4">
                  <div>
                    <h3 className="text-lg font-bold">{selectedPayslip.employee?.name}</h3>
                    <p className="text-slate-400 text-sm">{selectedPayslip.calculation.year}年{selectedPayslip.calculation.month}月分</p>
                  </div>
                  <Badge className={selectedPayslip.calculation.status === "confirmed" ? "bg-emerald-600" : "bg-amber-600"}>
                    {selectedPayslip.calculation.status === "confirmed" ? "確定" : "未確定"}
                  </Badge>
                </div>
                
                <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-4">
                  <div className="bg-slate-800 p-3 rounded">
                    <p className="text-slate-400 text-xs">出勤日数</p>
                    <p className="text-white font-bold">{selectedPayslip.calculation.working_days}日</p>
                  </div>
                  <div className="bg-slate-800 p-3 rounded">
                    <p className="text-slate-400 text-xs">労働時間</p>
                    <p className="text-white font-bold">{selectedPayslip.calculation.working_hours}h</p>
                  </div>
                  <div className="bg-slate-800 p-3 rounded">
                    <p className="text-slate-400 text-xs">残業時間</p>
                    <p className="text-amber-400 font-bold">{selectedPayslip.calculation.overtime_hours}h</p>
                  </div>
                  <div className="bg-slate-800 p-3 rounded">
                    <p className="text-slate-400 text-xs">深夜時間</p>
                    <p className="text-purple-400 font-bold">{selectedPayslip.calculation.late_night_hours}h</p>
                  </div>
                </div>
                
                <div className="grid md:grid-cols-2 gap-4">
                  <div>
                    <h4 className="text-emerald-400 font-medium mb-2 flex items-center gap-2">
                      <JapaneseYen className="w-4 h-4" />支給
                    </h4>
                    <div className="space-y-2 text-sm">
                      <div className="flex justify-between"><span className="text-slate-400">基本給</span><span>{formatCurrency(selectedPayslip.calculation.base_salary)}</span></div>
                      <div className="flex justify-between"><span className="text-slate-400">時間外手当</span><span>{formatCurrency(selectedPayslip.calculation.overtime_pay)}</span></div>
                      <div className="flex justify-between"><span className="text-slate-400">深夜手当</span><span>{formatCurrency(selectedPayslip.calculation.late_night_pay)}</span></div>
                      <div className="flex justify-between"><span className="text-slate-400">休日手当</span><span>{formatCurrency(selectedPayslip.calculation.holiday_pay)}</span></div>
                      <div className="flex justify-between"><span className="text-slate-400">通勤手当</span><span>{formatCurrency(selectedPayslip.calculation.commute_allowance)}</span></div>
                      <div className="flex justify-between"><span className="text-slate-400">その他手当</span><span>{formatCurrency(selectedPayslip.calculation.other_allowances)}</span></div>
                      <div className="flex justify-between pt-2 border-t border-slate-700 font-bold text-emerald-400">
                        <span>支給合計</span><span>{formatCurrency(selectedPayslip.calculation.gross_salary)}</span>
                      </div>
                    </div>
                  </div>
                  <div>
                    <h4 className="text-red-400 font-medium mb-2 flex items-center gap-2">
                      <JapaneseYen className="w-4 h-4" />控除
                    </h4>
                    <div className="space-y-2 text-sm">
                      <div className="flex justify-between"><span className="text-slate-400">健康保険</span><span>{formatCurrency(selectedPayslip.calculation.health_insurance)}</span></div>
                      <div className="flex justify-between"><span className="text-slate-400">介護保険</span><span>{formatCurrency(selectedPayslip.calculation.nursing_insurance)}</span></div>
                      <div className="flex justify-between"><span className="text-slate-400">厚生年金</span><span>{formatCurrency(selectedPayslip.calculation.pension)}</span></div>
                      <div className="flex justify-between"><span className="text-slate-400">雇用保険</span><span>{formatCurrency(selectedPayslip.calculation.employment_insurance)}</span></div>
                      <div className="flex justify-between"><span className="text-slate-400">所得税</span><span>{formatCurrency(selectedPayslip.calculation.income_tax)}</span></div>
                      <div className="flex justify-between"><span className="text-slate-400">住民税</span><span>{formatCurrency(selectedPayslip.calculation.resident_tax)}</span></div>
                      <div className="flex justify-between pt-2 border-t border-slate-700 font-bold text-red-400">
                        <span>控除合計</span><span>{formatCurrency(selectedPayslip.calculation.total_deductions)}</span>
                      </div>
                    </div>
                  </div>
                </div>
                
                <div className="mt-4 p-4 bg-emerald-900/30 border border-emerald-600 rounded-lg text-center">
                  <p className="text-slate-400 text-sm">差引支給額</p>
                  <p className="text-3xl font-bold text-emerald-400">{formatCurrency(selectedPayslip.calculation.net_salary)}</p>
                </div>
              </div>
            </div>
          )}
          
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowPayslipDialog(false)}>
              閉じる
            </Button>
            <Button onClick={printPayslip} className="bg-emerald-600 hover:bg-emerald-700">
              <Printer className="w-4 h-4 mr-2" />
              印刷
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default SalaryPage;
